<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ButtonV4 from 'next/button/Button.vue';

const props = defineProps({
  plan: {
    type: Object,
    required: true,
  },
  interval: {
    type: String,
    default: 'monthly',
  },
  isPending: {
    type: Boolean,
    default: false,
  },
  pendingEffectiveDate: {
    type: String,
    default: '',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  currentPlanName: {
    type: String,
    default: '',
  },
  currentBillingInterval: {
    type: String,
    default: 'monthly',
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const PLAN_RANK = {
  Starter: 1,
  Basic: 2,
  Pro: 3,
  Advanced: 4,
  Enterprise: 5,
};

const isYearlyInterval = computed(() => props.interval === 'yearly');

const isAnnualUnavailable = computed(() => {
  if (!isYearlyInterval.value) return false;
  if (props.plan.monthlyOnly) return true;
  if (props.plan.contactSales) return true;

  return !props.plan.basePriceYearly;
});

const formatPrice = value => {
  if (value == null) return null;

  const rounded = Math.round(Number(value) * 100) / 100;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(rounded);
};

const monthlyPrice = computed(() =>
  formatPrice(props.plan.basePriceMonthly)
);

const displayPrice = computed(() => {
  if (props.plan.contactSales) return null;

  if (isYearlyInterval.value && props.plan.basePriceYearly) {
    return formatPrice(props.plan.basePriceYearly / 12);
  }

  return monthlyPrice.value;
});

const showComparePrice = computed(
  () =>
    isYearlyInterval.value &&
    props.plan.basePriceYearly &&
    !props.plan.monthlyOnly &&
    !props.plan.contactSales
);

const billingSubtext = computed(() => {
  if (props.plan.contactSales) return null;

  if (
    isYearlyInterval.value &&
    props.plan.basePriceYearly &&
    !props.plan.monthlyOnly
  ) {
    return t('BILLING_SETTINGS.PLANS.BILLED_ANNUALLY', {
      amount: formatPrice(props.plan.basePriceYearly),
    });
  }

  return t('BILLING_SETTINGS.PLANS.BILLED_MONTHLY_NOTE');
});

const planDescriptionKey = computed(
  () => `BILLING_SETTINGS.PLANS.PLAN_DESCRIPTIONS.${props.plan.name.toUpperCase()}`
);

const planDescription = computed(() => {
  const key = planDescriptionKey.value;
  return t(key) === key ? null : t(key);
});

const isMostPopular = computed(() => props.plan.name === 'Pro');

const seatLabel = computed(() =>
  props.plan.seatCap
    ? t('BILLING_SETTINGS.PLANS.SEATS', { count: props.plan.seatCap })
    : t('BILLING_SETTINGS.PLANS.SEATS_UNLIMITED')
);

const isCurrentPlan = computed(
  () => props.currentPlanName === props.plan.name
);

const isCurrentPlanAndInterval = computed(
  () =>
    isCurrentPlan.value &&
    props.currentBillingInterval === props.interval
);

const isChooseDisabled = computed(
  () =>
    isCurrentPlanAndInterval.value ||
    props.isPending ||
    isAnnualUnavailable.value
);

const scheduledBadgeLabel = computed(() => {
  if (!props.isPending) return '';

  if (props.pendingEffectiveDate) {
    return t('BILLING_SETTINGS.PLANS.SCHEDULED_STARTS', {
      date: props.pendingEffectiveDate,
    });
  }

  return t('BILLING_SETTINGS.PLANS.SCHEDULED');
});

const actionLabel = computed(() => {
  if (props.isPending) return t('BILLING_SETTINGS.PLANS.SCHEDULED');
  if (isCurrentPlanAndInterval.value) {
    return t('BILLING_SETTINGS.PLANS.CURRENT');
  }

  if (isCurrentPlan.value) {
    return props.interval === 'yearly'
      ? t('BILLING_SETTINGS.PLANS.SWITCH_TO_ANNUAL')
      : t('BILLING_SETTINGS.PLANS.SWITCH_TO_MONTHLY');
  }

  if (!props.currentPlanName) return t('BILLING_SETTINGS.PLANS.CHOOSE');

  if (planRank.value > currentPlanRank.value) {
    return t('BILLING_SETTINGS.PLANS.UPGRADE_PLAN');
  }

  if (planRank.value < currentPlanRank.value) {
    return t('BILLING_SETTINGS.PLANS.DOWNGRADE');
  }

  return t('BILLING_SETTINGS.PLANS.CHOOSE');
});

const planRank = computed(() => PLAN_RANK[props.plan.name] ?? 0);

const currentPlanRank = computed(() => PLAN_RANK[props.currentPlanName] ?? 0);
</script>

<template>
  <div
    class="relative flex flex-col gap-4 p-5 rounded-xl border bg-n-solid-2"
    :class="[
      isCurrentPlanAndInterval ? 'border-n-brand ring-1 ring-n-brand' : 'border-n-weak',
      isPending ? 'border-n-amber-8 ring-1 ring-n-amber-8' : '',
      { 'opacity-60': isAnnualUnavailable },
    ]"
  >
    <span
      v-if="isMostPopular"
      class="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-medium px-3 py-1 rounded-full bg-n-brand text-white"
    >
      {{ $t('BILLING_SETTINGS.PLANS.MOST_POPULAR') }}
    </span>

    <div class="flex flex-col gap-1">
      <div class="flex items-center justify-between gap-2">
        <h3 class="text-lg font-semibold text-n-slate-12">{{ plan.name }}</h3>
        <div class="flex shrink-0 flex-col items-end gap-1">
          <span
            v-if="isCurrentPlanAndInterval"
            class="text-xs font-medium px-2 py-0.5 rounded-full bg-n-brand/10 text-n-brand"
          >
            {{ $t('BILLING_SETTINGS.PLANS.CURRENT') }}
          </span>
          <span
            v-if="isPending"
            class="text-xs font-medium px-2 py-0.5 rounded-full bg-n-amber-3 text-n-amber-11"
          >
            {{ scheduledBadgeLabel }}
          </span>
        </div>
      </div>
      <p v-if="planDescription" class="text-sm text-n-slate-11">
        {{ planDescription }}
      </p>
    </div>

    <div v-if="plan.contactSales" class="flex flex-col gap-1">
      <span class="text-3xl font-semibold text-n-slate-12">
        {{ $t('BILLING_SETTINGS.PLANS.CUSTOM') }}
      </span>
    </div>
    <div v-else class="flex flex-col gap-1">
      <div class="flex items-baseline gap-2">
        <span
          v-if="showComparePrice"
          class="text-lg text-n-slate-11 line-through"
        >
          €{{ monthlyPrice }}
        </span>
        <span
          class="text-3xl font-semibold text-n-slate-12"
          :class="{ 'text-n-slate-9': isAnnualUnavailable }"
        >
          €{{ displayPrice }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ $t('BILLING_SETTINGS.PLANS.PER_MO') }}
        </span>
      </div>
      <p class="text-sm text-n-slate-11">{{ billingSubtext }}</p>
    </div>

    <ButtonV4
      v-if="plan.contactSales"
      sm
      :faded="isAnnualUnavailable"
      :slate="isAnnualUnavailable"
      :solid="!isAnnualUnavailable"
      :blue="!isAnnualUnavailable"
      class="w-full"
      :disabled="isAnnualUnavailable"
      @click="emit('select', plan)"
    >
      {{ $t('BILLING_SETTINGS.PLANS.CONTACT_SALES') }}
    </ButtonV4>
    <ButtonV4
      v-else
      sm
      :solid="!isChooseDisabled"
      :blue="!isChooseDisabled"
      :faded="isChooseDisabled"
      :slate="isChooseDisabled"
      class="w-full"
      :is-loading="isLoading"
      :disabled="isChooseDisabled"
      @click="emit('select', plan)"
    >
      {{
        actionLabel
      }}
    </ButtonV4>

    <p
      v-if="isAnnualUnavailable"
      class="text-sm text-center text-n-slate-11"
    >
      {{ $t('BILLING_SETTINGS.PLANS.MONTHLY_BILLING_ONLY') }}
    </p>

    <div class="pt-2 border-t border-n-weak">
      <p class="mb-3 text-xs font-medium uppercase tracking-wide text-n-slate-11">
        {{ $t('BILLING_SETTINGS.PLANS.WHATS_INCLUDED') }}
      </p>
      <ul class="flex flex-col gap-2 text-sm text-n-slate-11">
        <li v-if="!plan.contactSales">
          {{
            $t('BILLING_SETTINGS.PLANS.INCLUDED_TICKETS', {
              count: plan.includedTickets,
            })
          }}
        </li>
        <li v-if="!plan.contactSales">
          {{
            $t('BILLING_SETTINGS.PLANS.OVERAGE', { rate: plan.overageRate })
          }}
        </li>
        <li>{{ seatLabel }}</li>
      </ul>
    </div>
  </div>
</template>
